/**
 * Tarjeta de resultado: el partido en una imagen que se pega en un grupo.
 *
 * Se dibuja en un canvas y sale como PNG. El formato es 1200x630, el de las
 * previsualizaciones de enlaces, para que se vea entera en cualquier chat.
 */
import "./resultCard.css";
import { t } from "../i18n/index.js";

const CARD_W = 1200;
const CARD_H = 630;

export interface ResultCardData {
  homeScore: number;
  awayScore: number;
  modeLabel: string;
  /** Lado del jugador, para saber que marcador es "el tuyo". */
  playerSide: "home" | "away";
  nickname: string;
  goals: number;
  assists: number;
  isMvp: boolean;
  /** "Victoria", "Derrota", "Empate". */
  outcome: string;
}

const GOLD = "#ffe082";
const INK = "#f2fff5";
const MUTED = "rgba(242, 255, 245, 0.62)";

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Dibuja la tarjeta. Separado del canvas para poder probarlo y reutilizarlo. */
export function drawResultCard(ctx: CanvasRenderingContext2D, data: ResultCardData): void {
  ctx.fillStyle = "#060b08";
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  const glow = ctx.createRadialGradient(CARD_W / 2, 0, 0, CARD_W / 2, 0, CARD_H * 1.2);
  glow.addColorStop(0, "rgba(255, 224, 130, 0.16)");
  glow.addColorStop(1, "rgba(255, 224, 130, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Franjas de cesped, muy tenues: identifica el juego sin robar atencion.
  ctx.fillStyle = "rgba(90, 200, 130, 0.035)";
  for (let x = 0; x < CARD_W; x += 120) ctx.fillRect(x, 0, 60, CARD_H);

  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = GOLD;
  ctx.font = "700 40px 'Bebas Neue', Impact, sans-serif";
  ctx.letterSpacing = "10px";
  ctx.fillText("FYNTA", 72, 96);
  ctx.letterSpacing = "0px";

  ctx.fillStyle = MUTED;
  ctx.font = "500 26px 'DM Sans', system-ui, sans-serif";
  ctx.textAlign = "right";
  ctx.fillText(data.modeLabel, CARD_W - 72, 96);
  ctx.textAlign = "left";

  // Marcador, SIEMPRE desde el punto de vista del jugador: en tu propia tarjeta
  // "3 - 1" tiene que querer decir que ganaste tu.
  const mine = data.playerSide === "home" ? data.homeScore : data.awayScore;
  const theirs = data.playerSide === "home" ? data.awayScore : data.homeScore;
  ctx.fillStyle = INK;
  ctx.font = "700 190px 'Bebas Neue', Impact, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(`${mine} - ${theirs}`, CARD_W / 2, 290);

  ctx.fillStyle = GOLD;
  ctx.font = "700 52px 'Bebas Neue', Impact, sans-serif";
  ctx.letterSpacing = "8px";
  ctx.fillText(data.outcome.toUpperCase(), CARD_W / 2, 356);
  ctx.letterSpacing = "0px";

  // Linea del jugador.
  const boxY = 410;
  ctx.fillStyle = "rgba(255, 255, 255, 0.05)";
  roundRect(ctx, 72, boxY, CARD_W - 144, 140, 8);
  ctx.fill();
  ctx.strokeStyle = "rgba(255, 224, 130, 0.3)";
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.textAlign = "left";
  ctx.fillStyle = INK;
  ctx.font = "700 44px 'Bebas Neue', Impact, sans-serif";
  ctx.fillText(data.nickname, 108, boxY + 62);

  if (data.isMvp) {
    ctx.fillStyle = GOLD;
    ctx.font = "700 26px 'Bebas Neue', Impact, sans-serif";
    ctx.letterSpacing = "4px";
    ctx.fillText("MVP DEL PARTIDO", 108, boxY + 104);
    ctx.letterSpacing = "0px";
  } else {
    ctx.fillStyle = MUTED;
    ctx.font = "500 22px 'DM Sans', system-ui, sans-serif";
    ctx.fillText(new Date().toLocaleDateString("es-ES", { day: "numeric", month: "long" }), 108, boxY + 102);
  }

  // Dos cifras a la derecha, grandes: goles y asistencias.
  const stat = (label: string, value: number, x: number): void => {
    ctx.textAlign = "center";
    ctx.fillStyle = value > 0 ? GOLD : MUTED;
    ctx.font = "700 76px 'Bebas Neue', Impact, sans-serif";
    ctx.fillText(String(value), x, boxY + 78);
    ctx.fillStyle = MUTED;
    ctx.font = "500 20px 'DM Sans', system-ui, sans-serif";
    ctx.fillText(label, x, boxY + 110);
  };
  stat("GOLES", data.goals, CARD_W - 300);
  stat("ASISTENCIAS", data.assists, CARD_W - 150);

  ctx.textAlign = "left";
}

export function renderResultCard(data: ResultCardData): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext("2d");
  if (ctx) drawResultCard(ctx, data);
  return canvas;
}

export interface ResultCardHandle {
  dispose(): void;
}

/**
 * Monta la tarjeta con sus botones.
 *
 * Copiar al portapapeles no funciona en todos los navegadores (Firefox no
 * admite imagenes en `ClipboardItem`), asi que descargar es el camino que
 * siempre esta: si copiar falla, se dice y queda el otro boton.
 */
export function mountResultCard(container: HTMLElement, data: ResultCardData): ResultCardHandle {
  const canvas = renderResultCard(data);
  canvas.className = "result-card-canvas";
  canvas.setAttribute("aria-label", t("Tarjeta del resultado"));

  container.classList.add("result-card");
  container.innerHTML = "";
  container.appendChild(canvas);

  /*
   * Tres caminos, y el orden importa porque el bueno depende del aparato.
   *
   *  1. **Compartir** (`navigator.share` con ficheros). Es el unico que abre
   *     WhatsApp / Instagram / lo que sea directamente, y es EL camino en
   *     movil — donde "copiar imagen" no lleva a ningun sitio util. Solo se
   *     pinta si el navegador de verdad sabe compartir ESTE fichero.
   *  2. **Copiar**, para pegar en un chat de escritorio.
   *  3. **Guardar**, que funciona siempre y es el suelo.
   */
  const canShareFile = (file: File): boolean => {
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    return typeof nav.share === "function" && nav.canShare?.({ files: [file] }) === true;
  };

  const actions = document.createElement("div");
  actions.className = "result-card-actions";
  actions.innerHTML = `
    <button type="button" class="result-card-btn is-primary" data-card-share hidden>${t("Compartir")}</button>
    <button type="button" class="result-card-btn" data-card-copy>${t("Copiar imagen")}</button>
    <button type="button" class="result-card-btn" data-card-save>${t("Guardar PNG")}</button>
  `;
  container.appendChild(actions);

  const shareBtn = actions.querySelector("[data-card-share]") as HTMLButtonElement;
  const copyBtn = actions.querySelector("[data-card-copy]") as HTMLButtonElement;
  const saveBtn = actions.querySelector("[data-card-save]") as HTMLButtonElement;
  let resetTimer = 0;

  const flash = (btn: HTMLButtonElement, text: string): void => {
    const original = btn.dataset.label ?? btn.textContent ?? "";
    btn.dataset.label = original;
    btn.textContent = text;
    btn.classList.add("is-done");
    clearTimeout(resetTimer);
    resetTimer = window.setTimeout(() => {
      btn.textContent = original;
      btn.classList.remove("is-done");
    }, 1800);
  };

  const toBlob = (): Promise<Blob | null> =>
    new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));

  const fileName = (): string => {
    const mine = data.playerSide === "home" ? data.homeScore : data.awayScore;
    const theirs = data.playerSide === "home" ? data.awayScore : data.homeScore;
    return `fynta-${mine}-${theirs}.png`;
  };

  // El boton de compartir solo aparece si el aparato sabe hacerlo con una
  // imagen. Enseñar un boton que va a fallar es peor que no tenerlo.
  void (async () => {
    const blob = await toBlob();
    if (!blob) return;
    if (canShareFile(new File([blob], fileName(), { type: "image/png" }))) {
      shareBtn.hidden = false;
    }
  })();

  shareBtn.addEventListener("click", () => {
    void (async () => {
      try {
        const blob = await toBlob();
        if (!blob) throw new Error("sin imagen");
        const file = new File([blob], fileName(), { type: "image/png" });
        await navigator.share({
          files: [file],
          title: "FYNTA",
          text: t("Mira este golazo. fynta.io"),
        });
      } catch (err) {
        // Cancelar la hoja de compartir NO es un fallo: no hay que decir nada.
        if ((err as DOMException)?.name === "AbortError") return;
        flash(shareBtn, t("No se pudo compartir"));
      }
    })();
  });

  copyBtn.addEventListener("click", () => {
    try {
      /*
       * El `ClipboardItem` se construye con la PROMESA del blob, sin `await`
       * delante.
       *
       * Safari exige que la escritura al portapapeles salga del gesto del
       * usuario, y un `await` antes de crearlo rompe esa cadena: el permiso ya
       * ha caducado cuando llega la llamada. Con la promesa dentro, el objeto
       * se crea en el mismo tick del clic y Safari lo acepta.
       */
      const item = new ClipboardItem({
        "image/png": toBlob().then((b) => {
          if (!b) throw new Error("sin imagen");
          return b;
        }),
      });
      void navigator.clipboard
        .write([item])
        .then(() => flash(copyBtn, t("¡Copiada!")))
        .catch(() => flash(copyBtn, t("Usa Guardar PNG")));
    } catch {
      // Firefox no admite imagenes en el portapapeles: queda Guardar PNG.
      flash(copyBtn, t("Usa Guardar PNG"));
    }
  });

  saveBtn.addEventListener("click", () => {
    void (async () => {
      const blob = await toBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName();
      a.click();
      // Liberar en el siguiente turno: revocarlo ya cancela la descarga.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      flash(saveBtn, "Guardada");
    })();
  });

  return {
    dispose() {
      clearTimeout(resetTimer);
      container.innerHTML = "";
    },
  };
}
